import React, { useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx"; // For PDF generation
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  InputAdornment,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  IconButton,
  Link,
  Avatar,
  Menu,
} from "@mui/material";
import "jspdf-autotable";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate, useParams } from "react-router-dom";
import { Menu as MenuIcon } from "@mui/icons-material";
import Header2 from "../../Component/Header2";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";

import autoTable from "jspdf-autotable";

const OrderReport = ({ userRole }) => {
  const { shopId } = useParams();
  const userShopId = shopId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortField, setSortField] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const userName = localStorage.getItem("username") || "Shopkeeper";
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const isProfileMenuOpen = Boolean(profileAnchorEl);

  // Fetch orders data based on the filter and role
  useEffect(() => {
    let endpoint = "https://sakhisshop.com/api/ordersreport";
    const params = [];

    // 🔥 get shopkeeper ID from localStorage or URL param
    const loggedShopId = localStorage.getItem("shopkeeperID") || shopId;

    // 🧠 Filter for shopkeeper
    if (userRole === "shopkeeper" && loggedShopId) {
      params.push(`sID=${loggedShopId}`);
    }

    // 🧠 Filter for status (Pending, Delivered, etc.)
    if (filterStatus !== "All") {
      params.push(`status=${filterStatus.toLowerCase()}`);
    }

    // ✅ Build endpoint with query params
    if (params.length > 0) {
      endpoint += "?" + params.join("&");
    }

    // ✅ Fetch data
    axios
      .get(endpoint)
      .then((res) => setOrders(res.data))
      .catch((err) => console.error("Error fetching order report:", err));
  }, [userRole, shopId, filterStatus]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // const handlePageChange = (page) => {
  //   setActivePage(page);
  //   if (isMobile) setMobileOpen(false);
  // };
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // Clear auth tokens or session storage if needed
    localStorage.clear(); // Or remove specific keys
    handleMenuClose();
    navigate("/MyAccountsSignIn"); // Redirect to login page
  };

  // Function to export the report as PDF
  const exportPDF = () => {
    console.log("PDF Export triggered ✅");

    const doc = new jsPDF();

    // 🏷️ Header
    doc.setFontSize(16);
    doc.text("🧾 Order Report", 14, 20);

    // 📅 Date and time
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 27);

    // 🧱 Headers setup
    const headers = [
      "Order ID",
      "Customer Name",
      "Order Date",
      ...(userRole === "admin" ? ["Shop Name"] : []),
      "Packing Option",
      "Payment Method",
      "Payment Status",
      "Total Amount ",
      "Status",
    ];

    // 🧩 Rows with clean numeric formatting
    const rows = sortedOrders.map((order) => [
      order.orderId ?? "-",
      order.customerName ?? "-",
      ...(userRole === "admin" ? [order.shopName ?? "-"] : []),
      order.packingOption ?? "-",
      order.paymentMethod ?? "-",
      order.paymentStatus ?? "-",
      // ✅ FIXED LINE: Ensure number is cleanly parsed before formatting
      `₹ ${Number(order.totalAmount ?? 0).toFixed(2)}`,
      order.status ?? "-",
    ]);

    // 🧾 Table
    autoTable(doc, {
      startY: 35,
      head: [headers],
      body: rows,
      theme: "striped",
      headStyles: {
        fillColor: [33, 150, 243], // Blue header
        textColor: [255, 255, 255],
        fontSize: 12,
        fontStyle: "bold",
      },
      bodyStyles: {
        fontSize: 10,
        cellPadding: 3,
      },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "left" },
        [headers.length - 2]: { halign: "right" }, // Align amount
        [headers.length - 1]: { halign: "center" }, // Align status
      },
    });

    // 💾 Save PDF
    doc.save("OrderReport.pdf");
  };

  // Function to export the report as Excel
  const exportExcel = () => {
    console.log("Excel Export triggered ✅");

    // Prepare data
    const headers = [
      "Order ID",
      "Customer Name",
      ...(userRole === "admin" ? ["Shop Name"] : []),
      "Packing Option",
      "Payment Method",
      "Payment Status",
      "Total Amount",
      "Status",
    ];

    const rows = sortedOrders.map((order) => [
      order.orderId,
      order.customerName,
      order.orderDate
        ? new Date(order.orderDate).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "N/A",
      ...(userRole === "admin" ? [order.shopName || "-"] : []),
      order.packingOption,
      order.paymentMethod,
      order.paymentStatus,
      `₹ ${Number(order.totalAmount).toFixed(2)}`,
      order.status,
    ]);

    // Create worksheet and workbook
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "OrderReport");

    // Download Excel file
    XLSX.writeFile(workbook, "OrderReport.xlsx");
  };
  const sortOrders = (orders, field, order) => {
    if (!field) return orders; // no sorting if not selected

    return [...orders].sort((a, b) => {
      if (field === "name") {
        let nameA = a.customerName?.toLowerCase() || "";
        let nameB = b.customerName?.toLowerCase() || "";
        return order === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }

      if (field === "date") {
        let dateA = new Date(a.orderDate);
        let dateB = new Date(b.orderDate);
        return order === "asc" ? dateA - dateB : dateB - dateA;
      }

      return 0;
    });
  };
  const sortedOrders = sortOrders(orders, sortField, sortOrder);
  const filteredOrders = sortedOrders.filter(
    (order) =>
      order.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.orderId?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Box>
      <Box p={{ xs: 2, sm: 4, md: 8, lg: 10 }} sx={{ overflowX: "auto" }}>
        {/* AppBar */}
        <Header2 />

        <Box
          sx={{ pt: { xs: 10, sm: 12 }, px: { xs: 2, sm: 4 } }}
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={2} // margin-bottom for spacing
        >
          <IconButton
            onClick={() => navigate(-1)} // Go back to previous page
            color="primary"
            sx={{ p: 0.5 }}
          >
            <ArrowBackIosNewIcon fontSize="large" />
          </IconButton>

          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              fontFamily: "Poppins",
              color: "#1b5e20",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
              py: 6,
            }}
          >
            📄 Order Report
          </Typography>
        </Box>

        {/* Filter Box – matches Sales Report design */}
        <Box
          sx={{
            backgroundColor: "white",
            p: 2,
            mb: 2,
            borderRadius: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search by Name or Order ID"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "300px", // 🔽 set fixed width for the search box
            }}
          />

          {/* Status Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Delivered">Delivered</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          {/* Sort Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              label="Sort By"
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="name">Customer Name</MenuItem>
              <MenuItem value="date">Order Date</MenuItem>
            </Select>
          </FormControl>

          {/* Order Asc/Desc */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              label="Order"
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>

          {/* Export Buttons */}
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#1976d2" }}
              onClick={exportPDF}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#2e7d32" }}
              onClick={exportExcel}
            >
              Export Excel
            </Button>
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead sx={{ backgroundColor: "#1976d2" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff" }}>Order ID</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Customer Name</TableCell>
                  <TableCell sx={{ color: "#fff" }}>OrderDate</TableCell>
                  {userRole === "admin" && (
                    <TableCell sx={{ color: "#fff" }}>Shop Name</TableCell>
                  )}{" "}
                  {/* Show ShopName only for admin */}
                  <TableCell sx={{ color: "#fff" }}>Packing Option</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Payment Method</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Payment Status</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Total Amount</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order, index) => (
                    <TableRow key={index}>
                      <TableCell>{order.orderId}</TableCell>
                      <TableCell>{order.customerName}</TableCell>
                      <TableCell>
                        {order.orderDate
                          ? new Date(order.orderDate).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              },
                            )
                          : "N/A"}
                      </TableCell>
                      {userRole === "admin" && (
                        <TableCell>{order.shopName}</TableCell>
                      )}{" "}
                      {/* Show ShopName only for admin */}
                      <TableCell>{order.packingOption}</TableCell>
                      <TableCell>{order.paymentMethod}</TableCell>
                      <TableCell>{order.paymentStatus}</TableCell>
                      <TableCell>₹ {order.totalAmount.toFixed(2)}</TableCell>
                      <TableCell>{order.status}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No Orders Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default OrderReport;
